/**
 * AI Generation API Service (SongForge)
 */

import { apiClient } from '../config';
import type {
  AIReferenceSong,
  AIGenerationQueueItem,
  AIPromptTemplate,
  CreateAIReferenceSongRequest,
  CreateAIGenerationQueueRequest,
  CreateAIPromptTemplateRequest,
  AIGenerationCoverage,
  AIGenerationStats,
  AIGenerationStatus,
  LinkedContent,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

export class AIGenerationService {
  // =========================================================================
  // Reference Songs
  // =========================================================================

  static async listReferences(params?: {
    organization_id?: string;
    genre?: string;
    mood?: string;
    search?: string;
  }): Promise<AIReferenceSong[]> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.genre) queryParams.set('genre', params.genre);
    if (params?.mood) queryParams.set('mood', params.mood);
    if (params?.search) queryParams.set('search', params.search);

    const response = await apiClient.get<ApiResponse<AIReferenceSong[]>>(
      `/api/songforge/references?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch reference songs');
    return response.data.data;
  }

  static async getReference(id: string): Promise<AIReferenceSong> {
    const response = await apiClient.get<ApiResponse<AIReferenceSong>>(
      `/api/songforge/references/${id}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch reference song');
    return response.data.data;
  }

  static async createReference(data: CreateAIReferenceSongRequest): Promise<AIReferenceSong> {
    const response = await apiClient.post<ApiResponse<AIReferenceSong>>(
      '/api/songforge/references',
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create reference song');
    return response.data.data;
  }

  static async updateReference(id: string, data: Partial<AIReferenceSong>): Promise<AIReferenceSong> {
    const response = await apiClient.put<ApiResponse<AIReferenceSong>>(
      `/api/songforge/references/${id}`,
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update reference song');
    return response.data.data;
  }

  static async deleteReference(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/songforge/references/${id}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete reference song');
  }

  // =========================================================================
  // Generation Queue
  // =========================================================================

  static async listQueue(params?: {
    organization_id?: string;
    status?: AIGenerationStatus;
    target_genre?: string;
    target_mood?: string;
  }): Promise<AIGenerationQueueItem[]> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.target_genre) queryParams.set('target_genre', params.target_genre);
    if (params?.target_mood) queryParams.set('target_mood', params.target_mood);

    const response = await apiClient.get<ApiResponse<AIGenerationQueueItem[]>>(
      `/api/songforge/queue?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch generation queue');
    return response.data.data;
  }

  static async getQueueItem(id: string): Promise<AIGenerationQueueItem> {
    const response = await apiClient.get<ApiResponse<AIGenerationQueueItem>>(
      `/api/songforge/queue/${id}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch queue item');
    return response.data.data;
  }

  static async createQueueItem(data: CreateAIGenerationQueueRequest): Promise<AIGenerationQueueItem> {
    const response = await apiClient.post<ApiResponse<AIGenerationQueueItem>>(
      '/api/songforge/queue',
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create queue item');
    return response.data.data;
  }

  static async updateQueueItem(id: string, data: Partial<AIGenerationQueueItem>): Promise<AIGenerationQueueItem> {
    const response = await apiClient.put<ApiResponse<AIGenerationQueueItem>>(
      `/api/songforge/queue/${id}`,
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update queue item');
    return response.data.data;
  }

  static async deleteQueueItem(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/songforge/queue/${id}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete queue item');
  }

  static async linkContent(
    queueItemId: string,
    contentId: string,
    options?: { variation_label?: string; set_as_primary?: boolean }
  ): Promise<AIGenerationQueueItem> {
    const response = await apiClient.post<ApiResponse<AIGenerationQueueItem>>(
      `/api/songforge/queue/${queueItemId}/link`,
      {
        content_id: contentId,
        variation_label: options?.variation_label,
        set_as_primary: options?.set_as_primary,
      }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to link content');
    return response.data.data;
  }

  static async unlinkContent(queueItemId: string, contentId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<{ unlinked: boolean }>>(
      `/api/songforge/queue/${queueItemId}/unlink/${contentId}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to unlink content');
  }

  static async setPrimaryTrack(queueItemId: string, contentId: string): Promise<void> {
    const response = await apiClient.post<ApiResponse<{ primary_set: boolean }>>(
      `/api/songforge/queue/${queueItemId}/set-primary/${contentId}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to set primary track');
  }

  static async getLinkedContent(queueItemId: string): Promise<LinkedContent[]> {
    const response = await apiClient.get<ApiResponse<LinkedContent[]>>(
      `/api/songforge/queue/${queueItemId}/content`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch linked content');
    return response.data.data;
  }

  // =========================================================================
  // Prompt Templates
  // =========================================================================

  static async listTemplates(params?: {
    organization_id?: string;
    genre?: string;
    mood?: string;
    is_active?: boolean;
  }): Promise<AIPromptTemplate[]> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.genre) queryParams.set('genre', params.genre);
    if (params?.mood) queryParams.set('mood', params.mood);
    if (params?.is_active !== undefined) queryParams.set('is_active', String(params.is_active));

    const response = await apiClient.get<ApiResponse<AIPromptTemplate[]>>(
      `/api/songforge/templates?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch templates');
    return response.data.data;
  }

  static async getTemplate(id: string): Promise<AIPromptTemplate> {
    const response = await apiClient.get<ApiResponse<AIPromptTemplate>>(
      `/api/songforge/templates/${id}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch template');
    return response.data.data;
  }

  static async createTemplate(data: CreateAIPromptTemplateRequest): Promise<AIPromptTemplate> {
    const response = await apiClient.post<ApiResponse<AIPromptTemplate>>(
      '/api/songforge/templates',
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create template');
    return response.data.data;
  }

  static async updateTemplate(id: string, data: Partial<AIPromptTemplate>): Promise<AIPromptTemplate> {
    const response = await apiClient.put<ApiResponse<AIPromptTemplate>>(
      `/api/songforge/templates/${id}`,
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update template');
    return response.data.data;
  }

  static async deleteTemplate(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/songforge/templates/${id}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete template');
  }

  static async incrementTemplateUsage(id: string): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/songforge/templates/${id}/use`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update template usage');
  }

  // =========================================================================
  // Analytics
  // =========================================================================

  static async getCoverage(organizationId?: string): Promise<AIGenerationCoverage> {
    const queryParams = new URLSearchParams();
    if (organizationId) queryParams.set('organization_id', organizationId);

    const response = await apiClient.get<ApiResponse<AIGenerationCoverage>>(
      `/api/songforge/coverage?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch coverage');
    return response.data.data;
  }

  static async getStats(organizationId?: string): Promise<AIGenerationStats> {
    const queryParams = new URLSearchParams();
    if (organizationId) queryParams.set('organization_id', organizationId);

    const response = await apiClient.get<ApiResponse<AIGenerationStats>>(
      `/api/songforge/stats?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch stats');
    return response.data.data;
  }
}
